import { useMemo, useState } from 'react';
import { Flex, Link, Tabs, TabsContent, TabsList, TabsTrigger } from '@optiaxiom/react';
import { useFeature } from '@optimizely/react-sdk';

import { PageHeader } from '@frontend/components';
import { useGetTools } from '@frontend/features/tools-management/hooks';
import { useIsToolsReadOnly, useObjectTitle, useOpalUser } from '@frontend/hooks';
import { AppPagePadding } from '@frontend/styles';
import { AuthProvider, ToolsManagementPage } from '@frontend/types';

import { AuthConnectionsTab } from './AuthConnections';
import { AuthProviderForm, AuthProvidersTab } from './AuthProviders';
import { ToolSearchInput } from './ToolSearchInput';
import { AddToolsRegistryForm, ToolsRegistryTab } from './ToolsRegistry';
import { ToolsTab } from './ToolsTab';

const TAB_TITLES: Record<ToolsManagementPage, string> = {
  tools: 'Tools',
  'tools-registries': 'Registries',
  'auth-connections': 'Auth Connections',
  'auth-providers': 'Auth Providers',
  'registry-form': 'Add Registry',
  'auth-provider-form': 'Auth Provider',
};

export function ToolsManagement() {
  const [filterText, setFilterText] = useState('');
  const { opalUser } = useOpalUser();
  const [, systemToolsConfig] = useFeature('manage_system_tools');
  const canManageAuthProviders = systemToolsConfig?.manage_auth_providers as boolean;
  const [isToolsAuthConnectionEnabled] = useFeature('tools_auth_connection');
  const isReadOnly = useIsToolsReadOnly();

  const { toolsResponse, isPending, error } = useGetTools(opalUser.instance.instance_id, true);
  const [activePage, setActivePage] = useState<ToolsManagementPage>(
    isReadOnly ? 'auth-connections' : 'tools'
  );
  const [editingProvider, setEditingProvider] = useState<AuthProvider | null>(null);

  // Set document title based on active tab
  useObjectTitle(TAB_TITLES[activePage]);

  const [activeServiceProvider, setActiveServiceProvider] = useState<string>('All');
  const [selectedStatus, setSelectedStatus] = useState<string>('All');
  const [selectedEnabledInPrimaryAgent, setSelectedEnabledInPrimaryAgent] = useState<string>('All');

  const serviceProviderNames = useMemo(
    () => [...new Set(toolsResponse?.functions.map(tool => tool.service.name) || [])],
    [toolsResponse]
  );

  if (activePage === 'registry-form') {
    return <AddToolsRegistryForm setActivePage={setActivePage} />;
  }

  if (activePage === 'auth-provider-form') {
    return (
      <AuthProviderForm
        setActivePage={page => {
          setActivePage(page);
          setEditingProvider(null);
        }}
        provider={editingProvider}
      />
    );
  }

  return (
    <Flex className={AppPagePadding} gap="24">
      <PageHeader
        title="Tools"
        headingText={
          <>
            Extend Opal&apos;s capabilities with connections across systems and services.{' '}
            <Link
              external
              href="https://support.optimizely.com/hc/en-us/sections/39340042035981-Tools"
            >
              Learn more.
            </Link>
          </>
        }
      />

      <Flex w="full">
        <Tabs
          onValueChange={value => setActivePage(value as ToolsManagementPage)}
          value={activePage}
        >
          {!isReadOnly && (
            <TabsList>
              <TabsTrigger value="tools">Tools</TabsTrigger>
              {isToolsAuthConnectionEnabled && (
                <TabsTrigger value="auth-connections">Connections</TabsTrigger>
              )}
              <TabsTrigger value="tools-registries">Registries</TabsTrigger>
              {canManageAuthProviders && (
                <TabsTrigger value="auth-providers">Auth Providers</TabsTrigger>
              )}
            </TabsList>
          )}

          {!isReadOnly && (
            <>
              <TabsContent py="16" value="tools">
                <Flex gap="16">
                  <ToolSearchInput
                    filterText={filterText}
                    handleFilterChange={setFilterText}
                    onClickAddToolRegistry={() => setActivePage('registry-form')}
                    serviceProviderNames={serviceProviderNames}
                    activeServiceProvider={activeServiceProvider}
                    onChangeServiceProvider={setActiveServiceProvider}
                    selectedStatus={selectedStatus}
                    onChangeStatus={setSelectedStatus}
                    selectedEnabledInPrimaryAgent={selectedEnabledInPrimaryAgent}
                    onChangeEnabledInPrimaryAgent={setSelectedEnabledInPrimaryAgent}
                    currentTab="tools"
                  />
                  <ToolsTab
                    filterText={filterText}
                    toolsResponse={toolsResponse}
                    isPending={isPending}
                    error={error}
                    activeServiceProvider={activeServiceProvider}
                    selectedStatus={selectedStatus}
                    selectedEnabledInPrimaryAgent={selectedEnabledInPrimaryAgent}
                  />
                </Flex>
              </TabsContent>

              <TabsContent py="16" value="tools-registries">
                <Flex gap="16">
                  <ToolSearchInput
                    filterText={filterText}
                    handleFilterChange={setFilterText}
                    onClickAddToolRegistry={() => setActivePage('registry-form')}
                    serviceProviderNames={serviceProviderNames}
                    activeServiceProvider={activeServiceProvider}
                    onChangeServiceProvider={setActiveServiceProvider}
                    selectedStatus={selectedStatus}
                    onChangeStatus={setSelectedStatus}
                    selectedEnabledInPrimaryAgent={selectedEnabledInPrimaryAgent}
                    onChangeEnabledInPrimaryAgent={setSelectedEnabledInPrimaryAgent}
                    currentTab="tools-registries"
                  />
                  <ToolsRegistryTab filterText={filterText} />
                </Flex>
              </TabsContent>
            </>
          )}

          {isToolsAuthConnectionEnabled && (
            <TabsContent py="16" value="auth-connections">
              <AuthConnectionsTab />
            </TabsContent>
          )}

          {canManageAuthProviders && (
            <TabsContent py="16" value="auth-providers">
              <AuthProvidersTab
                setActivePage={setActivePage}
                onEditProvider={provider => {
                  setEditingProvider(provider);
                  setActivePage('auth-provider-form');
                }}
              />
            </TabsContent>
          )}
        </Tabs>
      </Flex>
    </Flex>
  );
}
